import React, { useReducer, useEffect } from 'react';
import PropTypes from 'prop-types';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/RemoveCircle';

import GroupParticipantListItem from '../GroupParticipantListItem.container';

import { DialogTitle, DialogContent } from '../../../common/Dialog';
import InfiniteTable from '../../../common/InfiniteTable/InfiniteTable';

import {
  Text,
  BackButton,
  dialogStyle,
  infiniteListStyle,
} from './StudentNotInGroup.styled';

const StudentsNotInGroup = (props) => {
  const {
    studentsWithNoGroup,
    onBack,
    groupName,
    onSelect,
    onRemove,
    usersAdded,
    onClose,
  } = props;
  const [state, loadMore] = useReducer(
    (currentState) => {
      const batchSize = 10;
      const { lastItem = 0 } = currentState;
      const start = lastItem;
      const end = start + batchSize;
      const users = [
        ...currentState.users,
        ...currentState.studentsWithNoGroup.slice(start, end),
      ];
      const hasNext = users.length < studentsWithNoGroup.length;
      return {
        ...currentState,
        hasNext,
        users,
        lastItem: end,
      };
    },
    {
      hasNext: true,
      users: [],
      studentsWithNoGroup,
    }
  );

  useEffect(() => {
    loadMore();
  }, []);

  const { hasNext, users } = state;
  const missingStudentsToBeAdded = studentsWithNoGroup.filter(
    ({ id }) => !usersAdded[id]
  );
  const pluralizedStudents =
    missingStudentsToBeAdded.length === 1 ? 'student' : 'students';

  const addAction = {
    label: <AddIcon />,
    tooltip: 'Add to this group',
    onAction: onSelect,
  };

  const removeAction = {
    label: <RemoveIcon />,
    tooltip: 'Remove from this group',
    onAction: onRemove,
  };

  const missingStudentsText =
    missingStudentsToBeAdded.length === 0
      ? 'All students assigned to groups'
      : `${missingStudentsToBeAdded.length} ${pluralizedStudents} not yet in a group`;

  return (
    <>
      <DialogTitle id="back-to-editing-dialogtitle" onClose={onClose}>
        <BackButton onClick={onBack}>
          {`Back to editing ${groupName}`}
        </BackButton>
        <Text>{missingStudentsText}</Text>
      </DialogTitle>
      <DialogContent style={dialogStyle}>
        <InfiniteTable
          contentStyle={infiniteListStyle}
          viewProps={{ 'data-testid': 'students-not-in-a-group-list' }}
          items={users}
          keyExtractor={({ id }) => id}
          renderItem={(user) => (
            <GroupParticipantListItem
              key={user.id}
              user={user}
              action={usersAdded[user.id] ? removeAction : addAction}
              isReadOnly={false}
              userAdded={usersAdded[user.id]}
            />
          )}
          loadNext={loadMore}
          hasNext={hasNext}
          resetScrollOnMount
          animateItems={{
            enter: 'fade',
            leave: 'none',
          }}
        />
      </DialogContent>
    </>
  );
};

StudentsNotInGroup.propTypes = {
  studentsWithNoGroup: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  usersAdded: PropTypes.shape({
    [PropTypes.string]: PropTypes.bool,
  }).isRequired,
  onBack: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  groupName: PropTypes.string.isRequired,
};

export default StudentsNotInGroup;
